import type { GQL_ID } from '@@partner:types:graphql'
import type { Shop } from '@@partner:types:app'

import React, { useEffect, useState } from 'react'
import { CheckPicker, SelectPicker } from 'rsuite'
import { useLazyFetchShops } from '@@partner:graphql/query/shop/shops.gql'
import { getSearchableShops } from '@@partner:helpers/order'
import AppLoader from '@components/common/AppLoader'

interface ShopPickerItem {
  label: string
  value: GQL_ID
  shop?: Shop
}

interface BaseProps {
  placeholder?: string
  size?: 'lg' | 'md' | 'sm' | 'xs'
  searchable?: boolean
  block?: boolean
  disabled?: boolean
  className?: string
}

export interface ShopsPickerMultipleProps extends BaseProps {
  multiple: true
  value: GQL_ID[]
  onChange: (value: GQL_ID[]) => void
}

export interface ShopsPickerSingleProps extends BaseProps {
  multiple?: false
  value: GQL_ID | null
  onChange: (value: GQL_ID | null, shop?: Shop) => void
}

export type ShopsPickerProps = ShopsPickerMultipleProps | ShopsPickerSingleProps

function prepareShopsData (shops: Shop[]): ShopPickerItem[] {
  const searchable = getSearchableShops(shops)
  // Добавляем ссылку на оригинальный shop объект
  return searchable.map((item, index) => ({
    ...item,
    shop: shops.find(s => s.id === item.value)
  }))
}

export default function ShopsPicker (props: ShopsPickerProps) {
  const {
    placeholder = 'Магазины',
    size = 'sm',
    searchable = true,
    block = true,
    disabled,
    className
  } = props

  const [ shops, setShops ] = useState<ShopPickerItem[]>([])
  const [ fetchShops, { data, loading, called } ] = useLazyFetchShops()

  // Загружаем данные при маунте, если есть предзаполненное значение
  useEffect(() => {
    const hasValue = props.multiple 
      ? props.value?.length > 0 
      : props.value !== null && props.value !== undefined
    
    if (hasValue && !called) {
      fetchShops()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading && data) {
      setShops(prepareShopsData(data.shops))
    }
  }, [ loading, data ])

  const renderMenu = (menu: React.ReactNode) => {
    if (loading || !data) {
      return <AppLoader.Inline content="Загружаем" />
    }
    return menu
  }

  if (props.multiple) {
    return (
      <CheckPicker
        value={props.value}
        onChange={props.onChange}
        data={shops}
        onOpen={fetchShops}
        searchable={searchable}
        placeholder={placeholder}
        valueKey="value"
        labelKey="label"
        size={size}
        block={block}
        disabled={disabled}
        className={className}
        renderMenu={renderMenu}
      />
    )
  }

  const handleSingleChange = (value: GQL_ID | null, item: any) => {
    props.onChange(value, item?.shop)
  }

  return (
    <SelectPicker
      value={props.value}
      onChange={handleSingleChange}
      data={shops}
      onOpen={fetchShops}
      searchable={searchable}
      placeholder={placeholder}
      valueKey="value"
      labelKey="label"
      size={size}
      block={block}
      disabled={disabled}
      className={className}
      renderMenu={renderMenu}
    />
  )
}
